import React, { useState, useEffect } from 'react'
import { Button, Modal } from 'react-bootstrap'
import { css } from '@emotion/react'
import Joi from 'joi'
import GreenhouseApplicationForm from '@/components/SoleraNetwork/GreenhouseApplicationForm'

export const defaultEducation = {
  school_name_id: '',
  degree_id: '',
  discipline_id: '',
  start_date: {
    month: '',
    year: '',
  },
  end_date: {
    month: '',
    year: '',
  },
}

export const defaultValues = ghSrc => ({
  first_name: '',
  last_name: '',
  email: '',
  phone: '',
  location: '',
  latitude: '',
  longitude: '',
  question_31615216: '',
  resume: undefined,
  cover_letter: undefined,
  gender: '',
  race: '',
  veteran_status: '',
  disability_status: '',
  question_32620061: '', // linkedin
  question_31790786: '', // linkedin
  question_33165279: '', // linkedin
  question_32890642: '', // linkedin
  question_32620063: '', // salary
  question_31790787: '', // salary
  question_33165280: '', // salary
  question_32890644: '', // salary
  question_32620062: '', // h-1b status
  question_33165281: '', // infosec experience
  question_33165282: '', // infosec ed
  question_33165283: '', // Healthcare experience
  question_32908590: '', // Healthcare experience
  question_32908588: '', // Product management exp
  question_32908589: '', // Management exp
  // educations: [{ ...defaultEducation }],
  // employments: [],
  mapped_url_token: ghSrc || '',
  demographics_answers: [],
  data_compliance: {
    gdpr_consent_given: false,
  },
})

const monthYearSchema = yearOptional =>
  Joi.object({
    month: Joi.string().label('Month').allow('').optional(),
    year: !yearOptional
      ? Joi.string().label('Year')
      : Joi.string().label('Year').allow('').optional(),
  })

const edFields = [{ fields: [{ name: 'educations' }] }]

const fieldSchema = {
  first_name: Joi.string().label('First name').required(),
  last_name: Joi.string().label('Last name').required(),
  email: Joi.string()
    .label('Email')
    .email({ tlds: { allow: false } })
    .required(),
  phone: Joi.string()
    .label('Phone')
    .length(10)
    .pattern(/^[0-9]+$/)
    .required(),
  location: Joi.string().label('Location').required(),
  latitude: Joi.string().allow('').optional(),
  longitude: Joi.string().allow('').optional(),
  resume: Joi.object().label('Resume/CV').required(),
  resume_content: Joi.string().label('Resume/CV').required(),
  resume_content_filename: Joi.string(),
  resume_text: Joi.string(),
  cover_letter: Joi.object().label('Cover Letter'),
  cover_letter_content: Joi.string().label('Cover Letter').allow('').optional(),
  cover_letter_content_filename: Joi.string().allow('').optional(),
  cover_letter_text: Joi.string(),
  gender: Joi.string().label('Gender').allow('').optional(),
  race: Joi.string().label('Race').allow('').optional(),
  veteran_status: Joi.number().label('Veteran Status').allow('').optional(),
  disability_status: Joi.string()
    .label('Disability Status')
    .allow('')
    .optional(),
  // educations: Joi.array().items(
  //   Joi.object({
  //     school_name_id: Joi.string().label('School'),
  //     degree_id: Joi.string().label('Degree'),
  //     discipline_id: Joi.string().label('Discipline'),
  //     start_date: monthYearSchema(true).optional(),
  //     end_date: monthYearSchema(),
  //   })
  // ),
  mapped_url_token: Joi.string().allow('').optional(),
  demographics_answers: Joi.array().items(
    Joi.object({
      question_id: Joi.number(),
      answer_options: Joi.array().items(
        Joi.object({
          answer_option_id: Joi.number(),
          text: Joi.string().optional(),
        })
      ),
    })
  ),
  data_compliance: Joi.object({
    gdpr_consent_given: Joi.number()
      .label('GDPR Consent')
      .allow(1)
      .invalid(false)
      .required(),
  }).required(),
  question_31615216: Joi.string().required(),
  question_32620061: Joi.string()
    .uri()
    .label('LinkedIn Profile')
    .allow('')
    .optional(),
  question_31790786: Joi.string()
    .uri()
    .label('LinkedIn Profile')
    .allow('')
    .optional(),
  question_33165279: Joi.string().uri().label('LinkedIn Profile').required(),
  question_32890642: Joi.string().uri().label('LinkedIn Profile').required(),
  question_32620063: Joi.string().label('Salary Requirements').required(),
  question_31790787: Joi.string().label('Salary Requirements').required(),
  question_33165280: Joi.string().label('Salary Requirements').required(),
  question_32890644: Joi.string().label('Salary Requirements').required(),
  question_32620062: Joi.string().label('H-1B Visa Status').required(),
  question_32649187: Joi.string().label('DB Experience').required(),
  question_32649188: Joi.string().label('BI Experience').required(),
  question_33165281: Joi.string().label('Info Security Experience').required(),
  question_33165282: Joi.string().label('Info Security Education').required(),
  question_33165283: Joi.string()
    .label('Healthcare Industry Experience')
    .required(),
  question_32908590: Joi.string()
    .label('Healthcare Industry Experience')
    .required(),
  question_32908588: Joi.string()
    .label('Product Management Experience')
    .required(),
  question_32908589: Joi.string().label('Management Experience').required(),
  question_31615217: Joi.string(),
  question_31615218: Joi.string(),
  question_12345: Joi.string(),
  question_12346: Joi.number(),
  question_12348: Joi.array().items(Joi.number()),
  question_12349_url: Joi.string(),
  question_12349_url_filename: Joi.string(),
  question_12350_url: Joi.string(),
  question_12350_url_filename: Joi.string(),
  employments: Joi.array().items(
    Joi.object({
      company_name: Joi.string(),
      title: Joi.string(),
      start_date: monthYearSchema,
      end_date: monthYearSchema,
      current: Joi.boolean(),
    })
  ),
}

const JobForm = ({ job, onClose, ghSrc }) => {
  const [customValidationSchema, setCustomValidationSchema] = useState({})
  const jobQuestions = job?.questions || []
  const locationQuestions = job?.location_questions || []
  const compliance = job?.compliance?.flatMap(c => c.questions) || []
  const questions = [
    ...jobQuestions,
    ...locationQuestions,
    ...compliance,
    ...edFields,
  ]
  const fields = questions.flatMap(q =>
    q.fields ? q.fields.map(f => f.name) : []
  )

  useEffect(() => {
    const newSchema = fields.reduce((schema, field) => {
      if (field && fieldSchema[field]) {
        schema[field] = fieldSchema[field]
      } else {
        // Define a fallback schema for undefined fields
        schema[field] = Joi.string()
          .label('This field')
          .when('$otherField.required', { is: true, then: Joi.required() })
          .allow('')
          .optional()
      }
      return schema
    }, {})

    setCustomValidationSchema({
      ...newSchema,
      data_compliance: fieldSchema.data_compliance,
      mapped_url_token: fieldSchema.mapped_url_token,
      demographics_answers: fieldSchema.demographics_answers,
      location: fieldSchema.location,
      resume_content: fieldSchema.resume_content,
      resume_content_filename: fieldSchema.resume_content_filename,
      cover_letter_content: fieldSchema.cover_letter_content,
      cover_letter_content_filename: fieldSchema.cover_letter_content_filename,
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [job])

  if (!job?.questions) {
    console.warn('Could not render application')
    return null
  }

  return (
    <GreenhouseApplicationForm
      schema={Joi.object(customValidationSchema).unknown()}
      onClose={onClose}
      ghSrc={ghSrc}
      {...job}
    />
  )
}

const Dialog = ({ children, ...props }) => {
  const wide = css`
    max-width: 750px !important;
  `

  return (
    <Modal.Dialog css={wide} {...props}>
      {children}
    </Modal.Dialog>
  )
}

const GreenhouseApplication = ({ data: job, ghSrc }) => {
  const [show, setShow] = useState(false)

  const handleClose = () => setShow(false)
  const handleShow = () => setShow(true)

  return (
    <>
      <Button variant="success" className="mb-3" size="lg" onClick={handleShow}>
        Apply for this job
      </Button>

      <Modal show={show} onHide={handleClose} dialogAs={Dialog}>
        <Modal.Header closeButton>
          <Modal.Title>Apply for this job</Modal.Title>
        </Modal.Header>
        <JobForm onClose={handleClose} ghSrc={ghSrc} job={job} />
      </Modal>
    </>
  )
}

export default React.memo(GreenhouseApplication)
